import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import {
  ChevronRight, ChevronDown, Loader2, AlertTriangle, CheckCircle,
  Clock, Pause, MapPin, Plus, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_DOT = {
  completed: "bg-emerald-400",
  in_progress: "bg-amber-400 animate-pulse",
  pending: "bg-slate-300",
  paused: "bg-blue-300",
};

const STATUS_LABEL = {
  completed: "Done",
  in_progress: "Mining",
  pending: "Queued",
  paused: "Paused",
};

const STATUS_BADGE_BG = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-slate-50 text-slate-600 border-slate-200",
  paused: "bg-blue-50 text-blue-600 border-blue-200",
};

// ── Stats Bar ───────────────────────────────────────────
const StatsBar = ({ totals }) => {
  if (!totals) return null;
  const items = [
    { label: "Pending", value: totals.cities_pending, color: "text-slate-600", bg: "bg-slate-50 border-slate-200" },
    { label: "In Progress", value: totals.cities_in_progress, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
    { label: "Completed", value: totals.cities_completed, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  ];
  return (
    <div className="space-y-3" data-testid="pipeline-stats-bar">
      <div className="grid grid-cols-3 gap-2">
        {items.map((it) => (
          <div key={it.label} className={`rounded-xl border p-3 text-center ${it.bg}`}>
            <p className={`text-2xl font-black leading-none ${it.color}`}>{it.value}</p>
            <p className="text-[10px] font-semibold text-slate-500 mt-1">{it.label}</p>
          </div>
        ))}
      </div>
      {totals.active_city && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5" data-testid="active-pipeline-bar">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs font-bold text-amber-800">{totals.active_city}</span>
            </div>
            <span className="text-xs font-mono text-amber-600">{Math.round(totals.active_city_progress * 100)}%</span>
          </div>
          <div className="w-full h-1.5 bg-amber-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all duration-700"
              style={{ width: `${Math.round(totals.active_city_progress * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Problems Banner ─────────────────────────────────────
const ProblemsBanner = ({ problems }) => {
  if (!problems) return null;
  const issues = [];
  if (problems.dead_jobs > 0) issues.push(`${problems.dead_jobs} dead job${problems.dead_jobs > 1 ? "s" : ""}`);
  if (problems.failed_jobs_24h > 0) issues.push(`${problems.failed_jobs_24h} failed (24h)`);
  if (problems.stalled_cities?.length > 0) issues.push(`${problems.stalled_cities.length} stalled cit${problems.stalled_cities.length > 1 ? "ies" : "y"}`);
  if (problems.low_score_cities?.length > 0) issues.push(`${problems.low_score_cities.length} low-score`);
  if (issues.length === 0) return null;
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 flex items-start gap-2" data-testid="problems-banner">
      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-red-700 font-medium">{issues.join(" · ")}</div>
    </div>
  );
};

// ── City Row ────────────────────────────────────────────
const CityRow = ({ city }) => {
  const hasProblem = city.problems?.length > 0;
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] ${
        hasProblem ? "bg-red-50/60 border-red-200" : "bg-white border-slate-100"
      }`}
      data-testid={`city-row-${city.name}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[city.status]}`} />
      <span className="font-semibold text-slate-800 truncate min-w-0 flex-1">{city.name}</span>
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border flex-shrink-0 ${STATUS_BADGE_BG[city.status]}`}>
        {STATUS_LABEL[city.status]}
      </span>
      {city.completion_score != null && (
        <span className={`font-mono font-bold flex-shrink-0 ${
          city.completion_score >= 70 ? "text-emerald-600" : city.completion_score >= 40 ? "text-amber-600" : "text-red-600"
        }`}>
          {Math.round(city.completion_score)}
        </span>
      )}
      <span className="text-slate-400 flex-shrink-0 text-[10px]">{city.businesses_discovered}d/{city.businesses_researched}r</span>
      {hasProblem && (
        <span className="text-red-500 font-bold text-[10px] truncate max-w-[80px] flex-shrink-0" title={city.problems.join(", ")}>
          {city.problems[0]}
        </span>
      )}
    </div>
  );
};

// ── County Row (expandable) ─────────────────────────────
const CountyRow = ({ county }) => {
  const [open, setOpen] = useState(county.has_problems);
  return (
    <div className="ml-2 border-l-2 border-slate-100 pl-2">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-1.5 px-2 py-2 rounded-lg text-xs transition hover:bg-slate-50 ${
          county.has_problems ? "bg-red-50/40" : ""
        }`}
        data-testid={`county-row-${county.county}`}
      >
        {open ? <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0" />}
        <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
        <span className="font-semibold text-slate-700 text-left truncate min-w-0 flex-1">{county.county}</span>
        {county.has_problems && <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />}
        <span className="text-slate-500 font-mono text-[10px] flex-shrink-0">{county.completed}/{county.total_cities}</span>
        <span className="text-slate-400 text-[10px] flex-shrink-0">{county.businesses_discovered}d</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="space-y-1 pb-2 pl-2">
              {county.cities.map((city) => (
                <CityRow key={city.name} city={city} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── State Row (expandable) ──────────────────────────────
const StateRow = ({ stateData }) => {
  const [open, setOpen] = useState(stateData.has_problems);
  const pct = stateData.total_cities > 0
    ? Math.round((stateData.completed / stateData.total_cities) * 100) : 0;
  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        stateData.has_problems ? "border-red-200" : "border-slate-200"
      }`}
      data-testid={`state-row-${stateData.state}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-4 py-3 transition hover:bg-slate-50 ${
          stateData.has_problems ? "bg-red-50/30" : "bg-white"
        }`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="text-sm font-black text-slate-800">{stateData.state}</span>
        {stateData.has_problems && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
        <span className="flex-1" />
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-emerald-500" />
            <span className="font-bold text-emerald-600">{stateData.completed}</span>/{stateData.total_cities}
          </span>
          {stateData.in_progress > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-amber-500" />
              <span className="font-bold text-amber-600">{stateData.in_progress}</span>
            </span>
          )}
          {stateData.pending > 0 && (
            <span className="flex items-center gap-1">
              <Pause className="w-3 h-3 text-slate-400" />
              <span className="font-bold">{stateData.pending}</span>
            </span>
          )}
          <span className="font-mono text-slate-400">{pct}%</span>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-3 space-y-1 bg-slate-50/50">
              {stateData.counties.map((county) => (
                <CountyRow key={county.county} county={county} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Add City Modal ──────────────────────────────────────
const AddCityModal = ({ onClose, onAdded }) => {
  const [name, setName] = useState("");
  const [state, setState] = useState("");
  const [county, setCounty] = useState("");
  const [priority, setPriority] = useState(5);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !state.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/discovery/cities`, { name: name.trim(), state: state.trim(), county: county.trim() || null, priority });
      toast.success(`${name}, ${state} added to pipeline`);
      onAdded();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to add city";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800">Add City to Pipeline</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">City Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sacramento" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-400 focus:ring-1 focus:ring-sky-400 outline-none" data-testid="add-city-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">State</label>
              <input value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="CA" maxLength={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-400 focus:ring-1 focus:ring-sky-400 outline-none" data-testid="add-city-state" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Priority</label>
              <input type="number" value={priority} onChange={(e) => setPriority(parseInt(e.target.value) || 5)} min={1} max={10} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-400 focus:ring-1 focus:ring-sky-400 outline-none" data-testid="add-city-priority" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">County (optional)</label>
            <input value={county} onChange={(e) => setCounty(e.target.value)} placeholder="Sacramento County" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-sky-400 focus:ring-1 focus:ring-sky-400 outline-none" data-testid="add-city-county" />
          </div>
          <button
            type="submit"
            disabled={loading || !name.trim() || !state.trim()}
            className="w-full py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition disabled:opacity-50"
            data-testid="add-city-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Add City"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// ── Active Checkpoints Panel ────────────────────────────
const CheckpointsBar = ({ checkpoints }) => {
  if (!checkpoints || checkpoints.length === 0) return null;

  return (
    <div className="space-y-1.5" data-testid="checkpoints-panel">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Active Checkpoints</p>
      {checkpoints.map((cp) => {
        const pct = cp.checkpoint_businesses_total > 0
          ? Math.round((cp.checkpoint_businesses_completed / cp.checkpoint_businesses_total) * 100)
          : 0;
        const resumed = cp.checkpoint_business_index > 0;
        return (
          <div key={cp.job_id} className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2" data-testid={`checkpoint-${cp.job_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                <span className="text-[11px] font-bold text-sky-800 truncate">Job {cp.job_id.slice(-6)}</span>
                {resumed && (
                  <span className="text-[9px] font-bold bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded" data-testid={`checkpoint-resumed-${cp.job_id}`}>
                    Resumed
                  </span>
                )}
              </div>
              <span className="text-[11px] font-mono font-bold text-sky-600">{pct}%</span>
            </div>
            <div className="w-full h-1.5 bg-sky-200 rounded-full overflow-hidden mb-1">
              <div className="h-full bg-sky-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-sky-700">
                Business {cp.checkpoint_business_index} of {cp.checkpoint_businesses_total} — Sweep {cp.checkpoint_sweep_number}
              </span>
              <span className="text-[9px] text-sky-500">
                {cp.checkpoint_updated_at ? new Date(cp.checkpoint_updated_at).toLocaleTimeString() : ""}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ════════════════════════════════════════════════════════
// MAIN PANEL EXPORT
// ════════════════════════════════════════════════════════
export default function WorkflowPipelinePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCity, setShowAddCity] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pipeRes, cpRes] = await Promise.all([
        axios.get(`${API}/discovery/dashboard`),
        axios.get(`${API}/workflow/checkpoints`).catch(() => ({ data: [] })),
      ]);
      setData(pipeRes.data);
      setCheckpoints(cpRes.data || []);
      setLastRefreshedAt(Date.now());
    } catch (err) {
      console.error("Pipeline dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-xs text-slate-400 text-center py-4">Failed to load pipeline data</p>;

  return (
    <div className="space-y-3" data-testid="workflow-pipeline-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchData} lastRefreshedAt={lastRefreshedAt} testId="workflow-refresh" /></div>
      <StatsBar totals={data.totals} />
      <ProblemsBanner problems={data.problems} />
      <CheckpointsBar checkpoints={checkpoints} />

      {/* Add City Button */}
      <button
        onClick={() => setShowAddCity(true)}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-slate-200 text-xs font-bold text-slate-500 hover:border-slate-400 hover:text-slate-700 transition"
        data-testid="add-city-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Add City
      </button>

      {/* State > County > City Hierarchy */}
      {data.states?.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No cities in pipeline yet</p>
      ) : (
        <div className="space-y-2">
          {data.states.map((st) => (
            <StateRow key={st.state} stateData={st} />
          ))}
        </div>
      )}

      {showAddCity && <AddCityModal onClose={() => setShowAddCity(false)} onAdded={fetchData} />}
    </div>
  );
}
